import { useState, useCallback, useEffect, useMemo } from "react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useTranslation } from "react-i18next";
import {
    SideSheet,
    M3Button,
    FormSection,
    FieldGrid,
    M3Field,
    Chip,
    Banner,
} from "../m3";

/**
 * Inner Material 3 Form displaying all extracted fields across Scopes 1-3, Energy, Biogenic, and Metadata.
 */
export function DataExtractionPreviewForm({ editedData = {}, handleFieldEdit, ghgPrefill, handleGhgEdit }) {
    const { t } = useTranslation();

    const metadata = editedData.report_metadata || {};

    const hasScope1 = useMemo(() => {
        const s = editedData.scope1 || {};
        return Object.values(s).some((v) => v !== null && v !== undefined && v !== "");
    }, [editedData.scope1]);

    const hasScope2 = useMemo(() => {
        const s = editedData.scope2 || {};
        return Object.values(s).some((v) => v !== null && v !== undefined && v !== "");
    }, [editedData.scope2]);

    const hasScope3 = useMemo(() => {
        const s = editedData.scope3 || {};
        return Object.values(s).some((v) => v !== null && v !== undefined && v !== "");
    }, [editedData.scope3]);

    const hasEnergy = useMemo(() => {
        const e = editedData.energy || {};
        return Object.values(e).some((v) => v !== null && v !== undefined && v !== "");
    }, [editedData.energy]);

    const hasBiogenic = useMemo(() => {
        const b = editedData.biogenic || {};
        return Boolean(b.total !== null && b.total !== undefined && b.total !== "");
    }, [editedData.biogenic]);

    const primaryInfoVal = ghgPrefill?.primary_information ?? metadata.primary_information ?? (
        metadata.company_name ? `${metadata.company_name} — GHG Assessment (${metadata.reporting_year || ''})`.trim() : ""
    );
    const boundaryVal = ghgPrefill?.boundary || metadata.organizational_boundary || "Operational Control";
    const baseYearVal = ghgPrefill?.base_year ?? metadata.base_year ?? metadata.reporting_year ?? "";
    const clarificationVal = ghgPrefill?.clarification ?? metadata.clarification ?? "";
    const contextVal = ghgPrefill?.context ?? metadata.context ?? "";
    const exclusionVal = ghgPrefill?.exclusion ?? metadata.exclusion ?? "";
    const assumptionVal = ghgPrefill?.assumption ?? metadata.assumption ?? "";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* GHG Assessment Pre-fill Recommendation Banner */}
            {ghgPrefill && (ghgPrefill.base_year || ghgPrefill.boundary) && (
                <Banner tone="info">
                    <strong>{t("GHG Assessment Auto-fill")}: </strong>
                    {t("Base Year: {{year}} | Boundary: {{boundary}} | Scope 3: {{scope3}}", {
                        year: ghgPrefill.base_year || t("Current"),
                        boundary: ghgPrefill.boundary || t("Operational Control"),
                        scope3: ghgPrefill.is_scope_3_included ? t("Included") : t("Excluded"),
                    })}
                </Banner>
            )}

            {/* 1. GHG Emission Assessment Metadata */}
            <FormSection
                title={t("GHG Emission Assessment")}
                description={t("Define organizational boundaries, base year, and contextual accounting policies")}
            >
                <FieldGrid columns={2}>
                    <M3Field
                        label={t("Primary Information")}
                        value={primaryInfoVal}
                        onChange={(v) => {
                            if (handleGhgEdit) handleGhgEdit("primary_information", v);
                            handleFieldEdit("report_metadata", "primary_information", v);
                        }}
                        placeholder={t("Company name & reporting scope")}
                        span={2}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "13px", fontWeight: 500, color: "var(--m3-on-surface-variant)", fontFamily: "var(--m3-font)" }}>
                            {t("Organizational Boundaries")}
                        </label>
                        <select
                            value={boundaryVal}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (handleGhgEdit) handleGhgEdit("boundary", val);
                                handleFieldEdit("report_metadata", "organizational_boundary", val);
                            }}
                            style={{
                                height: "48px",
                                borderRadius: "8px",
                                border: "1px solid var(--m3-outline-variant, #cbd5e1)",
                                padding: "0 12px",
                                background: "var(--m3-surface, #ffffff)",
                                color: "var(--m3-on-surface, #1e293b)",
                                fontSize: "14px",
                                fontFamily: "var(--m3-font, 'Poppins', sans-serif)",
                            }}
                        >
                            <option value="Operational Control">{t("Operational Control")}</option>
                            <option value="Financial Control">{t("Financial Control")}</option>
                            <option value="Equity Share">{t("Equity Share")}</option>
                        </select>
                    </div>
                    <M3Field
                        label={t("Base Year")}
                        type="number"
                        value={baseYearVal}
                        onChange={(v) => {
                            if (handleGhgEdit) handleGhgEdit("base_year", v);
                            handleFieldEdit("report_metadata", "base_year", v);
                        }}
                        placeholder="2023"
                    />
                    <M3Field
                        label={t("Clarification")}
                        value={clarificationVal}
                        onChange={(v) => {
                            if (handleGhgEdit) handleGhgEdit("clarification", v);
                            handleFieldEdit("report_metadata", "clarification", v);
                        }}
                        placeholder={t("Clarification of company policy for base year recalculations")}
                    />
                    <M3Field
                        label={t("Context")}
                        value={contextVal}
                        onChange={(v) => {
                            if (handleGhgEdit) handleGhgEdit("context", v);
                            handleFieldEdit("report_metadata", "context", v);
                        }}
                        placeholder={t("Context for any significant emissions changes or disclosure source")}
                    />
                    <M3Field
                        label={t("Exclusion")}
                        value={exclusionVal}
                        onChange={(v) => {
                            if (handleGhgEdit) handleGhgEdit("exclusion", v);
                            handleFieldEdit("report_metadata", "exclusion", v);
                        }}
                        placeholder={t("Stated exclusions or excluded facilities")}
                    />
                    <M3Field
                        label={t("Assumption")}
                        value={assumptionVal}
                        onChange={(v) => {
                            if (handleGhgEdit) handleGhgEdit("assumption", v);
                            handleFieldEdit("report_metadata", "assumption", v);
                        }}
                        placeholder={t("Accounting assumptions and calculation standards")}
                    />
                </FieldGrid>
            </FormSection>

            {/* 2. Scope 1 Emissions */}
            <FormSection
                title={t("Scope 1 Emissions")}
                description={t("Direct emissions from fuel combustion, fleet vehicles, and on-site processes")}
                action={
                    <Chip tone={hasScope1 ? "success" : "neutral"} size="sm">
                        {hasScope1 ? t("Data Found") : t("Not Found")}
                    </Chip>
                }
            >
                <FieldGrid columns={2}>
                    <M3Field
                        label={t("Total Scope 1 Emissions")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope1?.total ?? ""}
                        onChange={(v) => handleFieldEdit("scope1", "total", v)}
                        placeholder="0.00"
                        span={2}
                    />
                    <M3Field
                        label={t("Stationary Combustion")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope1?.stationary_combustion ?? ""}
                        onChange={(v) => handleFieldEdit("scope1", "stationary_combustion", v)}
                        placeholder="0.00"
                        helper={t("Boilers, furnaces, generators")}
                    />
                    <M3Field
                        label={t("Mobile Combustion")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope1?.mobile_combustion ?? ""}
                        onChange={(v) => handleFieldEdit("scope1", "mobile_combustion", v)}
                        placeholder="0.00"
                        helper={t("Company-owned or leased vehicles")}
                    />
                    <M3Field
                        label={t("Fugitive Emissions")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope1?.fugitive_emissions ?? ""}
                        onChange={(v) => handleFieldEdit("scope1", "fugitive_emissions", v)}
                        placeholder="0.00"
                        helper={t("Refrigerants & fire suppression")}
                    />
                    <M3Field
                        label={t("Process Emissions")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope1?.process_emissions ?? ""}
                        onChange={(v) => handleFieldEdit("scope1", "process_emissions", v)}
                        placeholder="0.00"
                        helper={t("Industrial & chemical reactions")}
                    />
                </FieldGrid>
            </FormSection>

            {/* 3. Scope 2 Emissions */}
            <FormSection
                title={t("Scope 2 Emissions")}
                description={t("Indirect emissions from consumption of purchased electricity, steam, heat, or cooling")}
                action={
                    <Chip tone={hasScope2 ? "success" : "neutral"} size="sm">
                        {hasScope2 ? t("Data Found") : t("Not Found")}
                    </Chip>
                }
            >
                <FieldGrid columns={2}>
                    <M3Field
                        label={t("Total Scope 2 Emissions")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.total ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "total", v)}
                        placeholder="0.00"
                        span={2}
                    />
                    <M3Field
                        label={t("Location-Based Scope 2")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.location_based ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "location_based", v)}
                        placeholder="0.00"
                        helper={t("Grid-average emission factors")}
                    />
                    <M3Field
                        label={t("Market-Based Scope 2")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.market_based ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "market_based", v)}
                        placeholder="0.00"
                        helper={t("Contractual / supplier instruments")}
                    />
                    <M3Field
                        label={t("Purchased Electricity")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.purchased_electricity ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "purchased_electricity", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Purchased Heat")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.purchased_heat ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "purchased_heat", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Purchased Steam")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.purchased_steam ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "purchased_steam", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Purchased Cooling")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope2?.purchased_cooling ?? ""}
                        onChange={(v) => handleFieldEdit("scope2", "purchased_cooling", v)}
                        placeholder="0.00"
                    />
                </FieldGrid>
            </FormSection>

            {/* 4. Scope 3 Emissions (15 Categories) */}
            <FormSection
                title={t("Scope 3 Emissions")}
                description={t("Value chain emissions classified according to GHG Protocol Categories 1–15")}
                action={
                    <Chip tone={hasScope3 ? "success" : "neutral"} size="sm">
                        {hasScope3 ? t("Data Found") : t("Not Found")}
                    </Chip>
                }
            >
                <FieldGrid columns={2}>
                    <M3Field
                        label={t("Total Scope 3 Emissions")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.total ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "total", v)}
                        placeholder="0.00"
                        span={2}
                    />
                    <M3Field
                        label={t("Cat 1 — Purchased Goods & Services")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_1_purchased_goods ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_1_purchased_goods", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 2 — Capital Goods")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_2_capital_goods ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_2_capital_goods", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 3 — Fuel & Energy Activities")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_3_fuel_energy ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_3_fuel_energy", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 4 — Upstream Transportation")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_4_upstream_transport ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_4_upstream_transport", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 5 — Waste Generated in Operations")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_5_waste ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_5_waste", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 6 — Business Travel")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_6_business_travel ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_6_business_travel", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 7 — Employee Commuting")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_7_employee_commuting ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_7_employee_commuting", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 8 — Upstream Leased Assets")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_8_upstream_leased_assets ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_8_upstream_leased_assets", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 9 — Downstream Transportation")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_9_downstream_transport ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_9_downstream_transport", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 10 — Processing of Sold Products")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_10_processing_sold_products ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_10_processing_sold_products", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 11 — Use of Sold Products")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_11_use_of_sold_products ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_11_use_of_sold_products", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 12 — End-of-Life Treatment")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_12_end_of_life ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_12_end_of_life", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 13 — Downstream Leased Assets")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_13_downstream_leased_assets ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_13_downstream_leased_assets", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 14 — Franchises")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_14_franchises ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_14_franchises", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Cat 15 — Investments")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.scope3?.cat_15_investments ?? ""}
                        onChange={(v) => handleFieldEdit("scope3", "cat_15_investments", v)}
                        placeholder="0.00"
                    />
                </FieldGrid>
            </FormSection>

            {/* 5. Energy Consumption */}
            <FormSection
                title={t("Energy Consumption")}
                description={t("Total and broken down operational energy usage")}
                action={
                    <Chip tone={hasEnergy ? "success" : "neutral"} size="sm">
                        {hasEnergy ? t("Data Found") : t("Not Found")}
                    </Chip>
                }
            >
                <FieldGrid columns={2}>
                    <M3Field
                        label={t("Total Energy Consumption")}
                        suffix="MWh"
                        type="number"
                        value={editedData.energy?.total_mwh ?? ""}
                        onChange={(v) => handleFieldEdit("energy", "total_mwh", v)}
                        placeholder="0.00"
                        span={2}
                    />
                    <M3Field
                        label={t("Electricity Consumption")}
                        suffix="MWh"
                        type="number"
                        value={editedData.energy?.electricity_mwh ?? ""}
                        onChange={(v) => handleFieldEdit("energy", "electricity_mwh", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Natural Gas Consumption")}
                        suffix="MWh"
                        type="number"
                        value={editedData.energy?.natural_gas_mwh ?? ""}
                        onChange={(v) => handleFieldEdit("energy", "natural_gas_mwh", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Renewable Energy")}
                        suffix="MWh"
                        type="number"
                        value={editedData.energy?.renewable_mwh ?? ""}
                        onChange={(v) => handleFieldEdit("energy", "renewable_mwh", v)}
                        placeholder="0.00"
                    />
                    <M3Field
                        label={t("Non-Renewable Energy")}
                        suffix="MWh"
                        type="number"
                        value={editedData.energy?.non_renewable_mwh ?? ""}
                        onChange={(v) => handleFieldEdit("energy", "non_renewable_mwh", v)}
                        placeholder="0.00"
                    />
                </FieldGrid>
            </FormSection>

            {/* 6. Biogenic Emissions */}
            <FormSection
                title={t("Biogenic Emissions")}
                description={t("CO₂ emissions originating from biomass, biofuels, and biological sources")}
                action={
                    <Chip tone={hasBiogenic ? "success" : "neutral"} size="sm">
                        {hasBiogenic ? t("Data Found") : t("Not Found")}
                    </Chip>
                }
            >
                <FieldGrid columns={2}>
                    <M3Field
                        label={t("Total Biogenic Emissions")}
                        suffix="tCO₂e"
                        type="number"
                        value={editedData.biogenic?.total ?? ""}
                        onChange={(v) => handleFieldEdit("biogenic", "total", v)}
                        placeholder="0.00"
                        span={2}
                    />
                </FieldGrid>
            </FormSection>
        </div>
    );
}

/**
 * Data Extraction Preview SideSheet (100% Material 3 Component Kit)
 */
function DataExtractionPreview({ open, onClose, extractedData, ghgPrefill, analysisStats, onSave }) {
    const { t } = useTranslation();
    const [editedData, setEditedData] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (extractedData) {
            setEditedData(extractedData);
        }
    }, [extractedData]);

    const handleFieldEdit = useCallback((category, field, value) => {
        setEditedData((prev) => ({
            ...prev,
            [category]: {
                ...prev[category],
                [field]: value === "" ? null : value,
            },
        }));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            await onSave(editedData, ghgPrefill);
            onClose();
        } catch (error) {
            console.error("Error saving data:", error);
        } finally {
            setSaving(false);
        }
    }, [editedData, ghgPrefill, onSave, onClose]);

    const headerChips = analysisStats && analysisStats.pages ? (
        <Chip tone="primary" size="sm">
            {t("{{count}} Pages Analyzed", { count: analysisStats.pages })}
        </Chip>
    ) : null;

    const footerButtons = (
        <M3Button
            variant="filled"
            size="md"
            icon={<SaveOutlinedIcon style={{ fontSize: "16px" }} />}
            onClick={handleSave}
            loading={saving}
        >
            {saving ? t("Saving Data...") : t("Save Data to Inventory")}
        </M3Button>
    );

    const footerStartButton = (
        <M3Button variant="outlined" size="md" onClick={onClose} disabled={saving}>
            {t("Cancel")}
        </M3Button>
    );

    return (
        <SideSheet
            open={open}
            onClose={onClose}
            title={t("Extracted Data Preview")}
            subtitle={t("Review, edit, and import extracted greenhouse gas data into your inventory")}
            width={780}
            headerExtra={headerChips}
            footer={footerButtons}
            footerStart={footerStartButton}
        >
            <DataExtractionPreviewForm
                editedData={editedData}
                handleFieldEdit={handleFieldEdit}
                ghgPrefill={ghgPrefill}
            />
        </SideSheet>
    );
}

export default DataExtractionPreview;
